import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApplyMenuPriceFixDto,
  applySupportTicketMenuPriceFix,
  getSupportTicketMenuPriceFixes,
  queryKeys,
  SuccessToast,
  useBusyAction,
} from '@vgrubs/components';

/**
 * The prices a merchant says are wrong on their menu, and approving one.
 *
 * Asked for whenever a ticket opens: nothing on the ticket says whether it
 * has any. The socket invalidates this key on every event for the ticket (a
 * brand picked, an order attached, a colleague approving), so the dishes on
 * screen are always the latest answer - the server checks against exactly
 * that list on approve.
 */
export const useMenuPriceFixes = (ticketId: number) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.supportTickets.menuPriceFixes(ticketId),
    queryFn: async () => {
      const { data } = await getSupportTicketMenuPriceFixes(ticketId);
      return data.data;
    },
  });

  /* After any answer, good or bad: an approval moves the entry into
     `applied` and may claim the ticket, and every 400 here means the list or
     the ticket changed under us ("That dish is not one of the options"). */
  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.supportTickets.menuPriceFixes(ticketId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.supportTickets.detail(ticketId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.supportTickets.listAll,
    });
  };

  const { busy, run } = useBusyAction();

  const approve = async (dto: ApplyMenuPriceFixDto) => {
    const ok = await run(async () => {
      const { data } = await applySupportTicketMenuPriceFix(ticketId, dto);
      /* Saved either way. A publish that waits is a notice, not a failure:
         the price is in, someone still has to publish the menu. */
      SuccessToast(
        data.data.published
          ? 'Price updated and sent to the platforms.'
          : `Price saved, not published yet. ${
              data.data.publishNote ?? ''
            }`.trim()
      );
    });
    refresh();
    return ok;
  };

  return {
    fixes: query.data?.fixes ?? [],
    applied: query.data?.applied ?? [],
    busy,
    approve,
  };
};
